import { useMutation } from '@graphcommerce/graphql'
import { CustomerCartDocument } from '@graphcommerce/magento-cart/hooks/CustomerCart.gql'
import { UseMergeCustomerCartDocument } from '@graphcommerce/magento-cart/hooks/UseMergeCustomerCart.gql'
import { useAssignCurrentCartId } from '@graphcommerce/magento-cart/hooks/useAssignCurrentCartId'
import { useCurrentCartId } from '@graphcommerce/magento-cart/hooks/useCurrentCartId'
import { useCustomerQuery } from '@graphcommerce/magento-customer'
import { useContext, useEffect } from 'react'
import { globalContext } from '../../components/NextUi/globalContext'

/**
 * - Automatically assign the customer cart as the current cart
 * - Merge the guest cart into the customer cart
 */
export function useMergeCustomerCart() {
  const { currentCartId } = useCurrentCartId()
  const assignCurrentCartId = useAssignCurrentCartId()
  const [merge, { loading: mergeCartLoading }] = useMutation(UseMergeCustomerCartDocument, {
    errorPolicy: 'all',
    refetchQueries: ['CartPage', 'CustomerCart'],
  })

  const { setCartMergeInProgress } = useContext(globalContext)

  const { data, loading: customerQueryLoading } = useCustomerQuery(CustomerCartDocument, {
    fetchPolicy: 'no-cache',
  })

  const destinationCartId = data?.customerCart.id

  useEffect(() => {
    // If we don't have a customer cart, we're done
    // If the vistor cart is the same as the customer cart, we're done
    if (!destinationCartId || currentCartId === destinationCartId) return

    // Assign the customer cart as the new cart id
    assignCurrentCartId(destinationCartId)

    // If the visitor has a guest cart, try merging it into the customer cart
    if (currentCartId) {
      // We're not handling exceptions here:
      // If the merge returns an error, we'll use the customer cart without merging the guest cart.

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      merge({ variables: { sourceCartId: currentCartId, destinationCartId } })
    }
  }, [assignCurrentCartId, destinationCartId, merge, currentCartId, setCartMergeInProgress])

  useEffect(() => {
    setCartMergeInProgress(mergeCartLoading || customerQueryLoading)
  }, [mergeCartLoading, setCartMergeInProgress, customerQueryLoading])
}
